import React, { useEffect, useState, useCallback } from "react";
import { ChevronLeft, ChevronRight, Star, Quote, Check, RefreshCw } from "lucide-react";
import { motion, useMotionValue, animate } from "framer-motion";
import { api, asItems, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

// Issue #7: build display testimonials only from approved database reviews. No
// hardcoded/dummy content is ever shown; if there are no approved reviews the
// whole section is hidden by the component.
function getInitials(name) {
  if (!name) return "SH";
  const parts = String(name).trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return String(name).slice(0, 2).toUpperCase();
}

function formatCategory(category) {
  if (!category) return "Property";
  const map = {
    LAND: "Land",
    PLOT: "Plot",
    FLAT: "Apartment",
    HOUSE: "House",
    VILLA: "Villa",
    COMMERCIAL: "Commercial",
  };
  return map[String(category).toUpperCase()] || String(category);
}

function formatMonthYear(value) {
  if (!value) return "";
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleString("en-IN", { month: "long", year: "numeric" });
  } catch {
    return "";
  }
}

function mapReviewToTestimonial(r, idx) {
  const name = r.user_name || "Platform user";
  return {
    id: r.id || idx,
    name,
    role: "Buyer or owner",
    propertyType: formatCategory(r.category),
    city: r.city || "",
    date: formatMonthYear(r.created_at),
    quote: r.comment || "",
    rating: Math.max(1, Math.min(5, Number(r.rating) || 5)),
    initials: getInitials(name),
  };
}

function getDistance(index, activeIndex, total) {
  let diff = index - activeIndex;
  const half = Math.floor(total / 2);
  while (diff < -half) diff += total;
  while (diff > half) diff -= total;
  return diff;
}

export default function TestimonialsCarousel() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [selectedTestimonial, setSelectedTestimonial] = useState(null);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);

  const total = testimonials.length;
  const dragX = useMotionValue(0);

  // Fetch approved client stories from the database. No dummy fallback.
  useEffect(() => {
    let live = true;
    setLoading(true);
    setError("");
    api.get("/testimonials")
      .then((res) => {
        const items = asItems(res.data);
        if (live && Array.isArray(items)) {
          setTestimonials(items.map(mapReviewToTestimonial));
        }
      })
      .catch((requestError) => {
        if (live) {
          setTestimonials([]);
          setError(formatApiError(requestError));
        }
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => { live = false; };
  }, [reloadKey]);

  // Detect mobile viewports
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile, { passive: true });
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Detect reduced motion preferences
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    setPrefersReducedMotion(mediaQuery.matches);
    const handler = (e) => setPrefersReducedMotion(e.matches);
    mediaQuery.addEventListener("change", handler);
    return () => mediaQuery.removeEventListener("change", handler);
  }, []);

  const handlePrev = useCallback(() => {
    setActiveIndex((prev) => (total === 0 ? 0 : (prev - 1 + total) % total));
  }, [total]);

  const handleNext = useCallback(() => {
    setActiveIndex((prev) => (total === 0 ? 0 : (prev + 1) % total));
  }, [total]);

  // Autoplay functionality (5-7 seconds interval)
  useEffect(() => {
    if (total <= 1 || prefersReducedMotion || isPaused || isDragging) return;

    const interval = setInterval(() => {
      handleNext();
    }, 6000);

    return () => clearInterval(interval);
  }, [isPaused, isDragging, prefersReducedMotion, handleNext, total]);

  // Tab inactive visibility checks
  useEffect(() => {
    if (typeof document === "undefined") return;
    const handleVisibility = () => {
      if (document.hidden) setIsPaused(true);
      else setIsPaused(false);
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, []);

  const handleDragStart = () => {
    setIsDragging(true);
  };

  const handleDragEnd = (event, info) => {
    setIsDragging(false);
    const swipeThreshold = 50;
    const offset = info.offset.x;
    const velocity = info.velocity.x;
    
    if (offset < -swipeThreshold || velocity < -400) {
      handleNext();
    } else if (offset > swipeThreshold || velocity > 400) {
      handlePrev();
    }
    
    animate(dragX, 0, { type: "spring", stiffness: 300, damping: 30 });
  };

  const handleCardClick = (idx, diff, item) => {
    if (diff !== 0) {
      setActiveIndex(idx);
    } else {
      setSelectedTestimonial(item);
    }
  };

  if (loading) {
    return <section className="border-b border-slate-200/40 bg-slate-50/50 py-16" aria-label="Loading published reviews"><div className="mx-auto grid max-w-4xl gap-4 px-6 sm:grid-cols-2"><div className="h-40 animate-pulse rounded-3xl bg-slate-200/70"/><div className="h-40 animate-pulse rounded-3xl bg-slate-200/70"/></div></section>;
  }

  if (error) {
    return <section className="border-b border-slate-200/40 bg-slate-50/50 py-16" data-testid="testimonials-error"><div className="mx-auto max-w-xl rounded-3xl border border-amber-300/60 bg-amber-50 p-8 text-center" role="alert"><p className="text-sm font-bold text-slate-900">Published reviews could not be loaded.</p><p className="mt-1 text-xs text-slate-600">{error}</p><Button type="button" size="sm" variant="outline" className="mt-4 gap-2" onClick={() => setReloadKey((value) => value + 1)}><RefreshCw className="h-4 w-4" aria-hidden="true"/>Retry</Button></div></section>;
  }

  // No approved reviews yet -> hide the whole section (never show dummy content).
  if (total === 0) {
    return null;
  }

  return (
    <section className="py-16 border-b border-slate-200/40 select-none bg-slate-50/50 overflow-hidden" data-testid="testimonials-carousel" aria-label="Published property reviews">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
        
        {/* Header Container */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
          <div className="max-w-2xl border-l-4 border-[#0078DB] pl-4">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#0078DB] mb-1">
              Client Stories
            </p>
            <h2 className="font-display text-2xl sm:text-3xl font-black tracking-tight text-slate-900">
              Stories from Buyers
            </h2>
            <p className="text-xs sm:text-sm text-slate-500 mt-2 max-w-xl leading-relaxed">
              Read approved experiences submitted by users after a property interaction.
            </p>
          </div>
        </div>

        {/* Testimonials Carousel Center Area */}
        <div 
          className="relative w-full flex justify-center items-center overflow-visible py-10 px-0 md:px-14"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
          onKeyDown={(event) => {
            if (event.key === "ArrowLeft") { event.preventDefault(); handlePrev(); }
            if (event.key === "ArrowRight") { event.preventDefault(); handleNext(); }
          }}
          tabIndex={0}
          role="region"
          aria-label="Review carousel; use left and right arrow keys to navigate"
        >
          {/* Absolute Left Navigation Arrow (Vertically centered relative to cards) */}
          <button
            type="button"
            onClick={handlePrev}
            className="hidden md:flex absolute left-0 z-40 w-11 h-11 rounded-full bg-white shadow-lg border border-slate-100 items-center justify-center text-slate-700 hover:text-[#0078DB] hover:shadow-xl active:scale-95 transition transform -translate-y-1/2 top-1/2"
            aria-label="Previous testimonials"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>

          {/* Absolute Right Navigation Arrow (Vertically centered relative to cards) */}
          <button
            type="button"
            onClick={handleNext}
            className="hidden md:flex absolute right-0 z-40 w-11 h-11 rounded-full bg-white shadow-lg border border-slate-100 items-center justify-center text-slate-700 hover:text-[#0078DB] hover:shadow-xl active:scale-95 transition transform -translate-y-1/2 top-1/2"
            aria-label="Next testimonials"
          >
            <ChevronRight className="w-5 h-5" />
          </button>

          <motion.div
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.6}
            style={{ x: dragX }}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            className="relative flex items-center justify-center w-full h-[330px] md:h-[280px] cursor-grab active:cursor-grabbing overflow-visible"
          >
            {testimonials.map((item, idx) => {
              const diff = getDistance(idx, activeIndex, total);
              const isActive = diff === 0;
              const isVisible = Math.abs(diff) <= 1; // Only render active and side cards
              
              // Proportions
              const cardWidth = isMobile ? window.innerWidth * 0.85 : 660;
              const gap = isMobile ? 16 : 32;
              const step = isMobile ? cardWidth * 0.88 : cardWidth + gap;
              
              // Animation parameters
              const scaleValue = isActive ? 1 : (isMobile ? 0.9 : 0.88);
              const opacityValue = isActive ? 1 : (isMobile ? 0.65 : 0.55);
              const zIndexValue = isActive ? 10 : 5;
              const translateXValue = diff * step;

              return (
                <motion.div
                  key={item.id}
                  style={{
                    position: "absolute",
                    width: cardWidth,
                    left: "50%",
                    x: "-50%",
                    zIndex: zIndexValue,
                    pointerEvents: isVisible ? "auto" : "none",
                    transform: "translateZ(0)",
                    willChange: "transform, opacity",
                  }}
                  animate={{
                    translateX: translateXValue,
                    scale: scaleValue,
                    opacity: opacityValue,
                    y: isActive ? -8 : 0,
                  }}
                  transition={prefersReducedMotion ? { duration: 0 } : { type: "spring", stiffness: 280, damping: 28 }}
                  onClick={() => handleCardClick(idx, diff, item)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      handleCardClick(idx, diff, item);
                    }
                  }}
                  role="button"
                  tabIndex={isVisible ? 0 : -1}
                  aria-label={isActive ? `Open full review from ${item.name}` : `Show review from ${item.name}`}
                  className={`testimonial-card h-[290px] md:h-[240px] rounded-3xl border p-6 md:p-8 flex flex-col justify-between relative overflow-visible transition-shadow duration-300 ${
                    isActive 
                      ? "bg-white border-[#0078DB]/30 shadow-[0_20px_50px_rgba(0,120,219,0.08)]" 
                      : "bg-white border-slate-100 shadow-sm"
                  }`}
                >
                  {/* Top-Center Breakout Avatar Frame */}
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 rounded-full bg-gradient-to-br from-blue-50 to-[#0078DB]/20 text-[#0078DB] font-display font-black text-lg flex items-center justify-center border-4 border-white shadow-md z-20 select-none">
                    {item.initials}
                  </div>

                  {/* Subtle Quote Mark Icon */}
                  <Quote className="absolute right-6 top-6 w-8 h-8 text-slate-100 pointer-events-none" />

                  <div className="pt-8 text-center flex flex-col justify-between h-full relative z-10">
                    <div>
                      {/* Name & Verified Badge */}
                      <h4 className="font-display font-bold text-base text-slate-900 flex items-center justify-center gap-1.5 mb-1">
                        {item.name}
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-50 text-[9px] font-bold text-emerald-600 border border-emerald-100">
                          <Check className="w-2.5 h-2.5 stroke-[2.5px]" /> Published
                        </span>
                      </h4>

                      {/* Role Designation */}
                      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-3">{item.role}</p>

                      {/* Star Rating */}
                      <div className="flex justify-center gap-0.5 mb-3">
                        {[...Array(item.rating)].map((_, i) => (
                          <Star key={i} className="w-4 h-4 text-amber-400 fill-current" />
                        ))}
                      </div>

                      {/* Testimonial Quote */}
                      <p className={`text-slate-600 text-xs sm:text-sm font-medium leading-relaxed italic ${isActive ? "" : "line-clamp-3"}`}>
                        "{item.quote}"
                      </p>
                    </div>

                    {/* Bottom Metadata */}
                    <div className="mt-auto pt-4 border-t border-slate-100 text-[10px] font-semibold text-slate-400 flex flex-wrap items-center justify-center gap-1.5">
                      {[item.propertyType, item.city, item.date].filter(Boolean).map((value, metaIndex) => <React.Fragment key={`${value}-${metaIndex}`}>{metaIndex > 0 && <span className="text-slate-300">•</span>}<span>{value}</span></React.Fragment>)}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </div>

        {/* Pagination Dots */}
        <div className="flex justify-center gap-2 mt-8">
          {testimonials.map((_, idx) => (
            <button
              type="button"
              key={idx}
              onClick={() => setActiveIndex(idx)}
              className={`h-2 rounded-full transition-all duration-300 ${
                activeIndex === idx 
                  ? "w-6 bg-[#0078DB]" 
                  : "w-2 bg-slate-300 hover:bg-slate-400"
              }`}
              aria-label={`Go to testimonial ${idx + 1}`}
              aria-current={activeIndex === idx ? "true" : undefined}
            />
          ))}
        </div>

      </div>

      <Dialog open={Boolean(selectedTestimonial)} onOpenChange={(open) => { if (!open) setSelectedTestimonial(null); }}>
        {selectedTestimonial && <DialogContent className="max-w-lg overflow-hidden rounded-[32px] border-slate-100 bg-white p-8 shadow-2xl">
              {/* Decorative background quote */}
              <Quote className="absolute -right-4 -top-4 w-32 h-32 text-slate-50 opacity-40 pointer-events-none" />
              <div className="relative z-10 text-center">
                <DialogHeader className="sr-only"><DialogTitle>Review from {selectedTestimonial.name}</DialogTitle><DialogDescription>Full text and details for this published property review.</DialogDescription></DialogHeader>
                {/* Modal breakouts */}
                <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-blue-50 to-[#0078DB]/10 text-[#0078DB] font-extrabold grid place-items-center text-lg shadow-sm border border-[#0078DB]/5 mb-4">
                  {selectedTestimonial.initials}
                </div>

                {/* Modal rating */}
                <div className="flex justify-center gap-0.5 mb-5">
                  {[...Array(selectedTestimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-amber-400 fill-current" />
                  ))}
                </div>

                {/* Modal text */}
                <p className="text-slate-700 text-sm sm:text-base font-medium leading-relaxed italic mb-8">
                  "{selectedTestimonial.quote}"
                </p>

                {/* Modal user metadata */}
                <div className="pt-6 border-t border-slate-100">
                  <h4 className="font-display font-bold text-sm sm:text-base text-slate-900 flex items-center justify-center gap-2">
                    {selectedTestimonial.name}
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-[10px] font-bold text-emerald-600 border border-emerald-100">
                      <Check className="w-3 h-3 stroke-[2.5px]" /> {selectedTestimonial.role}
                    </span>
                  </h4>

                  <p className="text-xs font-semibold text-slate-400 mt-1 flex flex-wrap items-center justify-center gap-1.5">
                    {[selectedTestimonial.propertyType, selectedTestimonial.city, selectedTestimonial.date].filter(Boolean).map((value, metaIndex) => <React.Fragment key={`${value}-${metaIndex}`}>{metaIndex > 0 && <span className="text-slate-300">•</span>}<span>{value}</span></React.Fragment>)}
                  </p>
                </div>
              </div>

        </DialogContent>}
      </Dialog>
    </section>
  );
}
